import type { InputHTMLAttributes } from "react"

import {
  useFormContext,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type Path,
} from "react-hook-form"

import {
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form"
import { Input, PasswordInput } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type CustomFormInputProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  label?: string
  placeholder: string
  className?: string
  inputClassName?: string
  helpText?: string
  disabled?: boolean
  type?: string
  setValueAsString?: boolean
}

// Combine custom props with standard input attributes, omitting
// ones we handle explicitly or via react-hook-form
export type FormInputProps<T extends FieldValues = FieldValues> =
  CustomFormInputProps<T> &
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      // Attributes handled by react-hook-form's field object or FormField
      | "name"
      | "value"
      | "onChange"
      | "onBlur"
      | "ref"
      | "defaultValue"

      // Attributes handled explicitly by our props
      | "label"
      | "placeholder"
      | "className"
      | "inputClassName"
      | "disabled"
      | "setValueAsString"
    >

export function FormInput<T extends FieldValues = FieldValues>({
  name,
  label,
  placeholder,
  className,
  inputClassName,
  helpText,
  disabled = false,
  setValueAsString = false,
  ...otherHtmlAttributes
}: FormInputProps<T>) {
  const form = useFormContext<T>()
  const inputProps = (field: ControllerRenderProps<T, Path<T>>) => {
    return {
      placeholder: placeholder,
      className: inputClassName,
      // Other props (type, min, max, autoComplete, aria-*, data-*, etc.)
      ...otherHtmlAttributes,
      // Spread the field props from react-hook-form after ...rest to
      // ensure react-hook-form's value, onChange, onBlur, ref take
      // precedence
      ...field,
      // field.disabled might be set by RHF's Fieldset/Form context
      disabled: disabled || field.disabled,
    }
  }

  return (
    <FormField
      control={form.control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className={cn("w-full", className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            {otherHtmlAttributes.type === "password" ? (
              <PasswordInput {...inputProps(field)} />
            ) : (
              <Input
                {...inputProps(field)}
                onChange={(e) => {
                  if (otherHtmlAttributes.type === "url") {
                    const value = e.target.value.trim()
                    if (value && !/^https?:\/\//i.test(value)) {
                      // Add "https://" if not present
                      field.onChange(`https://${value}`)
                      return
                    }
                  } else if (otherHtmlAttributes.type === "number") {
                    const value =
                      e.target.value === ""
                        ? ""
                        : setValueAsString
                          ? e.target.value
                          : Number(e.target.value)

                    field.onChange(value)
                    return
                  }

                  field.onChange(e)
                }}
              />
            )}
          </FormControl>
          {helpText && <FormDescription>{helpText}</FormDescription>}
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}
